import React, { useMemo, useState } from 'react';
import { Tooltip, LineChart, ResponsiveContainer, Line, XAxis, ReferenceLine } from 'recharts';

import { Grid, Card, CardContent } from '@mui/material';
import { Typography, IconButton, Button } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Backdrop from '@material-ui/core/Backdrop';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import LockSharpIcon from '@material-ui/icons/LockSharp';

import { TooltipCard } from '.';
import InfoTooltip from '../Tooltips/InfoTooltip';
import { infoIconStyle } from '../../Dashboards/Market/styles/style';
import {
  COLOR_PRIMARY_DARK,
  COLOR_E,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_RED_LIGHT,
  COLOR_GREEN,
  COLOR_YELLOW,
  exchangeRates,
} from 'src/const';
import { DigestCardGraphData, Revenue } from 'src/interfaces/idashboard';
import { DigestCardPropTypes } from 'src/interfaces/iproptypes';
import mixpanel from 'mixpanel-browser';
import { convertCurrency, isEmpty, formatCurrency, parseGraphVal, getSeasonality } from 'src/utils/functions';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flex: 1,
    overflow: 'visible !important',
    [theme.breakpoints.up('sm')]: {
      height: 170,
    },
    [theme.breakpoints.up('lg')]: {
      height: 150,
    },
  },
  digestCardHeaderText: {
    fontSize: 17,
    fontWeight: 400,
  },
  successText: {
    color: theme.palette.success.main,
    fontWeight: 900,
  },
  iconButton: {
    padding: 3,
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 900,
  },
  rateText: {
    fontWeight: 900,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 100,
    background: '#fff4'
  },
  sampleText: {
    color: COLOR_E,
    fontWeight: 600,
  },
  linearProgress: {
    width: '100%',
    marginBottom: 20,
    color: COLOR_PRIMARY_DARK,
  },
}));

const loadingStyle = makeStyles((theme: Theme) => ({
  colorPrimary: {
    backgroundColor: (props: any) => props.backgroundColor,
  },
  barColorPrimary: {
    backgroundColor: COLOR_TOOLTIP_BACKGROUND,
  },
}));

const getChangeTooltip = (title: string) => {
  if (title.includes('Occupancy')) {
    return "This month's Occupancy Rate compared to last month's";
  }
  if (title.includes('Median Nightly Rate')) {
    return "This month's Median Nightly Rate compared to last month's";
  }
  if (title.includes('Revenue')) {
    return "This month's Revenue compared to last month's";
  }
  return '';
};

export const getYAxistName = (titleVal: string) => {
  if (titleVal.includes('Occupancy')) {
    return 'Occupancy Rate';
  }
  if (titleVal.includes('Nightly Rate')) {
    return 'Nightly Rate';
  }
  if (titleVal.includes('Revenue')) {
    return 'Revenue';
  }
  return '';
};

const formatRate = (rate: string, titleVal: string, currency: string) => {
  return formatCurrency(rate) + getUnit(titleVal, currency);
};

const getUnit = (titleVal: string, currency: string): string => {
  if (titleVal.includes('Occupancy')) {
    return '%';
  }
  if (titleVal.includes('Median Nightly Rate') || titleVal.includes('Revenue')) {
    return exchangeRates[currency].symbol;
  }
  return '';
};

const getMonthOverMonthChange = (
  graphData,
  title: string,
  isDisabled: boolean,
  yAxis: string,
  classes,
  currency: string
): string[] => {
  let text: string = '';
  let className: string = '';

  if (graphData.length == 0) {
    return [text, className];
  }

  const lastMonthData = graphData[graphData.length - 1][yAxis];
  const secondLastMonthData = graphData[graphData.length - 2][yAxis];
  if (typeof lastMonthData !== 'number' || typeof secondLastMonthData !== 'number') {
    return [text, className];
  }

  const deltaNum = (lastMonthData - secondLastMonthData).toFixed(2);
  const isPositive: boolean = parseFloat(deltaNum) >= 0;
  text = isPositive
    ? `+${formatRate(deltaNum, title, currency)}↑`
    : `${formatRate(deltaNum, title, currency)}↓`;
  className = isPositive ? classes.successText : classes.errorText;

  return [text, className];
};

const hoverGraph = () => {
  mixpanel.track('checkTooltipOnInvestmentDashboard');
};

const CustomTooltip = ({ active, payload, label, classes }) => {
  if (active && payload && payload.length > 0) {
    const _payload = payload[0].payload;
    if (
      _payload.Revenue === 'missing' ||
      _payload['Nightly Rate'] === 'missing' ||
      _payload['Occupancy Rate'] === 'missing'
    ) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {_payload.timePeriod}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Data Unavailable
            </Typography>
          </CardContent>
        </Card>
      );
    } else if (
      _payload.Revenue === 'inactive' ||
      _payload['Nightly Rate'] === 'inactive' ||
      _payload['Occupancy Rate'] === 'inactive'
    ) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {_payload.timePeriod}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Inactive
            </Typography>
          </CardContent>
        </Card>
      );
    } else {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {payload[0].payload.timePeriod}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {payload[0].name}: {payload[0].value}
              {payload[0].unit}
            </Typography>
          </CardContent>
        </Card>
      );
    }
  }

  return null;
};

const CustomizedDot = props => {
  //linechart
  const { cx, cy, value, color, getHighLowPoints } = props;

  const limit = getHighLowPoints();

  const size = 6;
  const smallSize = 4;

  if (value === limit.high || value === limit.low) {
    return (
      <svg x={cx - size} y={cy - size} width={size * 2} height={size * 2} fill="white">
        <g transform={`translate(${size} ${size})`}>
          <circle r={size} fill={color} />
          {/* <circle r={size / 2} fill="white" /> */}
        </g>
      </svg>
    );
  } else if (value !== null && value >= 0) {
    return (
      <svg
        x={cx - smallSize}
        y={cy - smallSize}
        width={smallSize * 2}
        height={smallSize * 2}
        fill="white"
      >
        <g transform={`translate(${smallSize} ${smallSize})`}>
          <circle r={smallSize} fill={color} />
          <circle r={smallSize / 2} fill="white" />
        </g>
      </svg>
    );
  } else {
    return (
      <svg
        x={cx - smallSize}
        y={cy - smallSize}
        width={smallSize * 2}
        height={smallSize * 2}
        fill="white"
      >
        <g transform={`translate(${smallSize} ${smallSize})`}>
          <circle r={smallSize} fill={'#0000'} />
        </g>
      </svg>
    );
  }
};

const DigestCard = ({
  title,
  data,
  color,
  isDisabled,
  isLoggedIn,
  multipleMarkersCallLoading,
  dynamicTrigger,
  setSubscriptionModalOpen,
  currency,
  user,
  setShowSearchCreditDeductWarningModal,
  setLoginModalOpen,
  allowDemoDate,
  isAnnualRevenue,
  showDeltaText,
  handleOnSampleClick,
  isFromHostInsights,
  isFromListingHistory,
}: DigestCardPropTypes) => {
  const classes = useStyles();
  const { colorPrimary, barColorPrimary } = loadingStyle({ backgroundColor: color });

  //to make tooltip responsive both desktop and mobile
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  //current active point on the graph
  //Linechart

  const getHighLowPoints = () => {
    let data_title = '';
    if (title === 'Occupancy Rate') {
      data_title = 'occupancy';
    } else if (title === 'Revenue') {
      data_title = 'revenue';
    } else if (title === 'Median Nightly Rate') {
      data_title = 'adr';
    }
    let low = Infinity;
    let high = -Infinity;
    data.slice(-12).forEach(data => {
      if (data[data_title] != 'missing' && data[data_title] != 'inactive') {
        if (high < data[data_title]) {
          high = data[data_title];
        }
        if (low > data[data_title]) {
          low = data[data_title];
        }
      }
    });
    return { high, low };
  };

  const getStartingEndingDate = (date: any, full: boolean = false): string => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    if (!date) return '';

    const yearMonthArray = date.split('-');
    const year = yearMonthArray[0];
    const fullMonth = monthNames[~~yearMonthArray[1] - 1]; //~~ -> converts into an integer
    const shortMonth = fullMonth.substring(0, 3);
    return full ? fullMonth + ', ' + year : shortMonth + ', ' + year;
  };

  let yAxis: string = getYAxistName(title);
  let rate: string = '';
  
  const [graphData] = useMemo(() => {
    return [parseGraphVal(data, yAxis, currency)];
  }, [data, currency]);
  let seasonalityData = {} as {
    highestRevenue: Revenue;
    lowestRevenue: Revenue;
    seasonality: number;
  };
  
  // need refactoring and cleaning this module
  if (title.includes('Occupancy')) {
    let occupancyRateSum: number = 0;
    let no_of_months: number = 0;
    graphData.slice(-12).forEach((data: any) => {
      if (data['Occupancy Rate'] !== null && (data['Occupancy Rate'] as number) >= 0) {
        occupancyRateSum = occupancyRateSum + (data['Occupancy Rate'] as number);
        no_of_months += 1;
      }
    });
    rate = (occupancyRateSum / no_of_months).toFixed(0);
  }

  if (title.includes('Median Nightly Rate')) {
    let averageDailyRateSum: number = 0;
    let no_of_months: number = 0;
    graphData.slice(-12).forEach((data: any) => {
      if (data['Nightly Rate'] !== null && (data['Nightly Rate'] as number) >= 0) {
        averageDailyRateSum = averageDailyRateSum + (data['Nightly Rate'] as number);
        no_of_months += 1;
      }
    });
    rate = (averageDailyRateSum / no_of_months).toFixed(0);
  }

  if (title.includes('Revenue')) {
    let revenueSum: number = 0;
    let no_of_months: number = 0;
    graphData.slice(-12).forEach((data: any) => {
      if (data['Revenue'] !== null && (data['Revenue'] as number) >= 0) {
        revenueSum = revenueSum + (data['Revenue'] as number);
        no_of_months += 1;
      }
    });

    if (isAnnualRevenue) {
      rate = revenueSum.toFixed(0);
    } else {
      rate = (revenueSum / no_of_months).toFixed(0);
    }

    if (graphData.length > 0) {
      seasonalityData = getSeasonality(graphData);
    }
  }

  const [deltaText, deltaClass] = getMonthOverMonthChange(
    graphData,
    title,
    isDisabled,
    yAxis,
    classes,
    currency
  );

  let blurGraph = {};
  if (isDisabled) {
    blurGraph = {
      // webkitFilter: 'blur(2px)',
      filter: 'opacity(0.4)'
    };
  }
  const tooltipObj = {
    'Median Nightly Rate': isFromHostInsights
      ? [
          "The past 12 months' average nightly rate of listings operated by this host.",
          'It does not include cleaning fee, service fee, or extra guest fee.',
          'Only months having data points are taken into consideration while calculating average.',
        ]
      : [
          `The past 12 months' average nightly rate of listings on the map.`,
          `It doesn't include a cleaning fee, service fee, or extra guest fee.`,
          `Only months having data points are taken into consideration while calculating average.`,
        ],
    'Occupancy Rate': isFromHostInsights
      ? [
          `The past 12 months' average occupancy rate of listings operated by this host.`,
          `Occupancy Rate = Number of booked days / Number of available days`,
          `Only months having data points are taken into consideration while calculating average.`,
        ]
      : [
          `The past 12 months' average occupancy rate of listings on the map.`,
          `Occupancy Rate = Number of booked days / Number of available days`,
          `Only months having data points are taken into consideration while calculating average.`,
        ],
    Revenue: isFromHostInsights
      ? [
          `The past 12 months' total revenue of listings operated by this host.`,
          `Revenue = ((Nightly Rate + Extra Guest Fees) x Number of Booked Nights) + (Cleaning Fees x Number of bookings)`,
          `Only months having data points are taken into consideration while calculating total value.`,
        ]
      : [
          `The past 12 months' average revenue of listings on the map.`,
          `Revenue = ((Nightly Rate + Extra Guest Fees) x Number of Booked Nights) + (Cleaning Fees x Number of bookings)`,
          `Only months having data points are taken into consideration while calculating average.`,
        ],
  };

  if (isFromListingHistory) {
    tooltipObj['Median Nightly Rate'] = [
      "The past 12 months' average nightly rate of this listing.",
      'It does not include cleaning fee, service fee, or extra guest fee.',
      'Only months having data points are taken into consideration while calculating average.',
    ];

    tooltipObj['Occupancy Rate'] = [
      `The past 12 months' average occupancy rate of this listings.`,
      `Occupancy Rate = Number of booked days / Number of available days`,
      `Only months having data points are taken into consideration while calculating average.`,
    ];

    tooltipObj['Revenue'] = [
      `The past 12 months' total revenue of this listings.`,
      `Revenue = ((Nightly Rate + Extra Guest Fees) x Number of Booked Nights) + (Cleaning Fees x Number of bookings)`,
      `Only months having data points are taken into consideration while calculating total value.`,
    ];
  }

  return (
    <Card
      className={classes.root}
      style={{ paddingBottom: multipleMarkersCallLoading ? '40px' : 0 }}
    >
      <CardContent>
        <Grid container justifyContent="space-around">
          {multipleMarkersCallLoading && (
            <Grid item className={classes.linearProgress} style={{ marginBottom: '20px' }}>
              <LinearProgress
                classes={{ colorPrimary: colorPrimary, barColorPrimary: barColorPrimary }}
              />
            </Grid>
          )}
          {dynamicTrigger && !multipleMarkersCallLoading && (
            <Grid item className={classes.linearProgress} style={{ marginBottom: '20px' }}>
              <LinearProgress
                classes={{ colorPrimary: colorPrimary, barColorPrimary: barColorPrimary }}
              />
            </Grid>
          )}
          <Grid item xs={5} container direction="column">
            {/* justifyContent="space-between" */}
            <Typography variant="h2" className={classes.digestCardHeaderText}>
              {(isFromHostInsights || isFromListingHistory) && title === 'Revenue'
                ? 'Annual Revenue'
                : title}
              <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                <InfoTooltip
                  title={
                    <TooltipCard
                      title={title}
                      content={tooltipObj[title][0]}
                      contentAdditional={tooltipObj[title][1]}
                      contentAdditional2={tooltipObj[title] ? tooltipObj[title][2] : ''}
                      link="https://support.airbtics.com/"
                    />
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  open={tooltipOpen}
                  onOpen={() => setTooltipOpen(true)}
                  onClose={() => setTooltipOpen(false)}
                >
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => setTooltipOpen(prev => !prev)}
                  >
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </ClickAwayListener>
            </Typography>

            {isDisabled ? (
              <IconButton
                onClick={() => {
                  if (!isLoggedIn && setLoginModalOpen) {
                    setLoginModalOpen(true);
                  } else if (user && user.credits > 0 && setShowSearchCreditDeductWarningModal) {
                    setShowSearchCreditDeductWarningModal(true);
                  } else {
                    setSubscriptionModalOpen(true);
                  }
                }}
              >
                <LockSharpIcon style={{ fontSize: 40, color: '#4aa59a' }} />
              </IconButton>
            ) : (
              <Typography className={classes.rateText} variant="h4" component="h3">
                {formatRate(rate, title, currency)}
              </Typography>
            )}

            {title.includes('Revenue') &&
              !isDisabled &&
              showDeltaText &&
              !isEmpty(seasonalityData) && (
                <InfoTooltip title={<TooltipCard content={getChangeTooltip(title)} />}>
                  <Typography className={deltaClass}>{deltaText}</Typography>
                </InfoTooltip>
              )}
          </Grid>

          <Grid item container xs={6} justifyContent="flex-end" spacing={0}>
            <Grid item xs={1} container>
              {title.includes('Revenue') && !isDisabled && !isEmpty(seasonalityData) && (
                <Grid>
                  <InfoTooltip
                    title={
                      <TooltipCard
                        title={`Seasonality: ${seasonalityData.seasonality}%`}
                        content={`Highest Revenue: ${
                          exchangeRates[currency].symbol
                        }${formatCurrency(
                          String(
                            // convertCurrency(
                            //   currency,
                              seasonalityData.highestRevenue?.revenue
                            // )
                          )
                        )} (${seasonalityData.highestRevenue?.date})`}
                        contentAdditional={`Lowest Revenue: ${
                          exchangeRates[currency].symbol
                        }${formatCurrency(
                          String(
                            // convertCurrency(
                            //   currency,
                              seasonalityData.lowestRevenue?.revenue
                            // )
                          )
                        )} (${seasonalityData.lowestRevenue?.date})`}
                      />
                    }
                  >
                    <div
                      style={{
                        width: 10,
                        borderRadius: 10,
                        height: `${seasonalityData.seasonality}%`,
                        background: getSeasonalityBarColor(seasonalityData.seasonality),
                      }}
                    ></div>
                  </InfoTooltip>
                </Grid>
              )}
            </Grid>
            <Grid
              container
              item
              xs={11}
              direction="row"
              style={{ position: 'relative', top: '0', left: '0' }}
            >
              <Grid item xs={12}>
                <ResponsiveContainer height={100} width="100%">
                  <LineChart
                    data={graphData.slice(-12)}
                    style={blurGraph}
                    onMouseEnter={hoverGraph}
                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                  >
                    <Line
                      animationDuration={150}
                      isAnimationActive={false}
                      type="monotone"
                      dataKey={yAxis}
                      stroke={color}
                      strokeWidth={3}
                      unit={getUnit(title, currency)}
                      dot={<CustomizedDot color={color} getHighLowPoints={getHighLowPoints} />}
                    />
                    <XAxis dataKey="timePeriod" hide={true} />
                    <ReferenceLine
                      x={
                        data.length > 0
                          ? getStartingEndingDate(data[data.length - 12].date, true)
                          : ''
                      }
                      stroke={color}
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeDasharray="4 12"
                    />
                    <ReferenceLine
                      x={
                        data.length > 0
                          ? getStartingEndingDate(data[data.length - 1].date, true)
                          : ''
                      }
                      stroke={color}
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeDasharray="4 12"
                    />
                    <Tooltip
                      position={{ x: 0, y: 80 }}
                      content={<CustomTooltip classes={classes} active payload label />}
                      wrapperStyle={{ zIndex: 100000 }}
                      filterNull={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{
                  fontFamily: 'roboto',
                  fontSize: '10px',
                  color: '#555',
                }}
              >
                {(!isDisabled || allowDemoDate) && (
                  <>
                    <Grid item>
                      {data.length > 0 && getStartingEndingDate(data[data.length - 12].date)}
                    </Grid>
                    <Grid item>
                      {data.length > 0 && getStartingEndingDate(data[data.length - 1].date)}
                    </Grid>
                  </>
                )}
              </Grid>

              {isDisabled && (
                <Backdrop className={classes.backdrop} open={true} invisible={true}>
                  <Typography variant="h5" className={classes.sampleText}>
                    <Button
                      onClick={() => {
                        if (handleOnSampleClick) {
                          handleOnSampleClick();
                        } else if (!isLoggedIn && setLoginModalOpen) {
                          setLoginModalOpen(true);
                        } else if (
                          user &&
                          user.credits > 0 &&
                          setShowSearchCreditDeductWarningModal
                        ) {
                          setShowSearchCreditDeductWarningModal(true);
                        } else {
                          setSubscriptionModalOpen(true);
                        }
                      }}
                      style={{ all: 'unset', cursor: 'pointer' }}
                    >
                      PREMIUM
                    </Button>
                  </Typography>
                </Backdrop>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// const getSeasonality = (graphData: DigestCardGraphData[]) => {
//   let seasonalityData = {} as {
//     highestRevenue: Revenue;
//     lowestRevenue: Revenue;
//     seasonality: number;
//   };
  
//   const revenues: Revenue[] = graphData.map(data => {
//     return { date: data.timePeriod, revenue: data.Revenue as number | 'missing' | 'inactive' };
//   });

//   const filteredRevenues = revenues.filter(
//     revenue => revenue.revenue !== 'missing' && revenue.revenue !== 'inactive'
//   );
//   const sortedRevenues = [...filteredRevenues].sort((a, b) => {
//     return (a.revenue as number) - (b.revenue as number);
//   });
//   seasonalityData.highestRevenue = sortedRevenues[sortedRevenues.length - 1];
//   seasonalityData.lowestRevenue = sortedRevenues[0];

//   if (sortedRevenues.length >= 6) {
//     const highRevenues: number[] = [
//       sortedRevenues[sortedRevenues.length - 1].revenue as number,
//       sortedRevenues[sortedRevenues.length - 2].revenue as number,
//       sortedRevenues[sortedRevenues.length - 3].revenue as number,
//     ];
//     const lowRevenues: number[] = [
//       sortedRevenues[0].revenue as number,
//       sortedRevenues[1].revenue as number,
//       sortedRevenues[2].revenue as number,
//     ];
//     const avgHigh =
//       highRevenues.reduce((partial_sum: number, revenue: number) => partial_sum + revenue, 0) /
//       highRevenues.length;
//     const avgLow =
//       lowRevenues.reduce((partial_sum: number, revenue: number) => partial_sum + revenue, 0) /
//       lowRevenues.length;

//     seasonalityData.seasonality = Math.round(((avgHigh - avgLow) / avgHigh) * 100);
//   } else {
//     const highestRevenue = sortedRevenues[sortedRevenues.length - 1].revenue as number;
//     const lowestRevenue = sortedRevenues[0].revenue as number;
//     seasonalityData.seasonality = Math.round(
//       ((highestRevenue - lowestRevenue) / highestRevenue) * 100
//     );
//   }

//   return seasonalityData;
// };

const getSeasonalityBarColor = (seaosnality: number) => {
  if (seaosnality <= 50) return COLOR_GREEN;
  else if (seaosnality <= 75) return COLOR_YELLOW;
  else return COLOR_RED_LIGHT;
};

const processTimePeriod = (datestring: string) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const yearMonthArray = datestring.split('-');
  const year = yearMonthArray[0];
  const fullMonth = monthNames[~~yearMonthArray[1] - 1]; //~~ -> converts into an integer
  const shortMonth = fullMonth.substring(0, 3);
  return fullMonth + ', ' + year;
};

export default DigestCard;
